import { Hospital, ShoppingCart, CreditCard, BarChart3, Users, Building2, Building, Shield, GraduationCap, Truck } from "lucide-react";

export const TEMPLATES = [
  {
    id: "healthcare", icon: Hospital, title: "Healthcare Patient Management",
    tag: "HIGHEST DEMAND", tagColor: "#FF3B30",
    desc: "Patient records, appointments, diagnoses, billing, prescriptions",
    prompt: "A healthcare clinic. Tables: patients, doctors, appointments, diagnoses, prescriptions, billing, insurance_claims, lab_results. Include realistic medical codes, insurance providers, and patient demographics."
  },
  {
    id: "ecommerce", icon: ShoppingCart, title: "E-commerce & Retail",
    tag: "POPULAR", tagColor: "#FFCC00",
    desc: "Products, orders, customers, inventory, reviews — full retail stack",
    prompt: "An e-commerce store. Tables: customers, products, categories, orders, order_items, inventory, product_reviews, shipping. Realistic product names across electronics, fashion, and home categories with appropriate pricing."
  },
  {
    id: "saas", icon: CreditCard, title: "SaaS Subscription & Billing",
    tag: "POPULAR", tagColor: "#FFCC00",
    desc: "Users, plans, subscriptions, invoices, usage tracking, support tickets",
    prompt: "A B2B SaaS platform. Tables: organizations, users, plans, subscriptions, invoices, payment_methods, usage_logs, support_tickets. Starter/Pro/Enterprise tiers, MRR data, realistic company names."
  },
  {
    id: "crm", icon: BarChart3, title: "CRM & Sales Pipeline",
    tag: "TRENDING", tagColor: "#A855F7",
    desc: "Leads, contacts, opportunities, activities, deals, quotations",
    prompt: "A B2B CRM system. Tables: companies, contacts, leads, opportunities, pipeline_stages, activities, quotations, deal_history. Realistic deal sizes, win/loss ratios, and industry verticals."
  },
  {
    id: "hr", icon: Users, title: "HR & People Analytics",
    tag: "TRENDING", tagColor: "#A855F7",
    desc: "Employees, departments, payroll, attendance, performance reviews",
    prompt: "A 500-employee company HR system. Tables: employees, departments, positions, salary_history, attendance, performance_reviews, leave_requests, expense_claims. Realistic salary bands."
  },
  {
    id: "fintech", icon: Building2, title: "Banking & Financial Services",
    tag: "HIGH DEMAND", tagColor: "#FF3B30",
    desc: "Accounts, transactions, loans, KYC records, fraud flags",
    prompt: "A retail bank. Tables: customers, accounts, transactions, loans, loan_payments, kyc_documents, cards, fraud_alerts. Realistic financial data with appropriate currency and transaction patterns."
  },
  {
    id: "realestate", icon: Building, title: "Real Estate & Property",
    tag: "POPULAR", tagColor: "#FFCC00",
    desc: "Properties, agents, listings, transactions, tenants",
    prompt: "A real estate agency. Tables: properties, property_types, agents, listings, inquiries, rental_contracts, sales_transactions, maintenance_requests. Realistic property data with local area names and market-appropriate pricing."
  },
  {
    id: "insurance", icon: Shield, title: "Insurance Claims Processing",
    tag: "ENTERPRISE", tagColor: "#00E5A0",
    desc: "Policies, claims, assessments, customers, agents, payouts",
    prompt: "An insurance company. Tables: customers, agents, policy_types, policies, claims, claim_assessments, premium_payments, renewals. Auto/health/property/life types, realistic claims."
  },
  {
    id: "edtech", icon: GraduationCap, title: "EdTech Student Management",
    tag: "GROWING", tagColor: "#6366F1",
    desc: "Students, courses, enrollments, grades, instructors, assignments",
    prompt: "An online learning platform. Tables: students, instructors, courses, modules, enrollments, assignments, submissions, grades. Data Science, Web Dev, AI/ML, Cloud topics."
  },
  {
    id: "logistics", icon: Truck, title: "Supply Chain & Logistics",
    tag: "ENTERPRISE", tagColor: "#00E5A0",
    desc: "Shipments, warehouses, suppliers, inventory, purchase orders, routes",
    prompt: "A logistics company. Tables: suppliers, warehouses, products, inventory, purchase_orders, shipments, delivery_routes, transfers. Realistic supply chain data with appropriate shipping routes and container types."
  },
];

export const COUNTRY_CODES = [
  { code: "+971", label: "+971 (UAE)" },
  { code: "+1", label: "+1 (US)" },
  { code: "+44", label: "+44 (UK)" },
  { code: "+91", label: "+91 (IN)" },
  { code: "+65", label: "+65 (SG)" },
  { code: "+49", label: "+49 (DE)" },
  { code: "+33", label: "+33 (FR)" },
  { code: "+81", label: "+81 (JP)" },
  { code: "+86", label: "+86 (CN)" },
  { code: "+61", label: "+61 (AU)" },
  { code: "+55", label: "+55 (BR)" },
  { code: "+966", label: "+966 (SA)" },
  { code: "+974", label: "+974 (QA)" },
  { code: "+973", label: "+973 (BH)" },
  { code: "+968", label: "+968 (OM)" },
  { code: "+234", label: "+234 (NG)" },
  { code: "+254", label: "+254 (KE)" },
  { code: "+27", label: "+27 (ZA)" },
];

export const USE_CASES = [
  "NLP-to-SQL Chatbots", "Voice Agents", "BI Dashboards", "RAG Apps",
  "CRM Copilots", "Healthcare AI", "FinTech Apps", "HR Automation",
  "Real Estate Bots", "Insurance AI", "EdTech Platforms", "Supply Chain AI"
];

export const CHATGPT_COMPARISONS = [
  {
    icon: "GitBranch",
    title: "Multi-table relational integrity",
    text: "ChatGPT gives you one flat table. MockDB generates 5+ interconnected tables where foreign keys actually resolve. Try JOINing ChatGPT's output."
  },
  {
    icon: "Globe",
    title: "Deep domain accuracy",
    text: 'ChatGPT gives you "John Doe, $100". MockDB gives you ICD-10 codes, context-appropriate pricing, and realistic MRR distributions. Data your prospect\'s domain expert won\'t laugh at.'
  },
  {
    icon: "BarChart3",
    title: "Statistical consistency at scale",
    text: "Ask ChatGPT for 1000 rows and watch it hallucinate duplicates, break distributions, and lose referential integrity. MockDB maintains statistical consistency across every row."
  },
  {
    icon: "Zap",
    title: "One click, no prompt engineering",
    text: "No back-and-forth fixing broken JSON, no manual stitching of tables, no reformatting into SQL/CSV. Describe once, get a complete database."
  },
  {
    icon: "Download",
    title: "Production-ready exports",
    text: "CSV, JSON, SQL — ready to load into your app immediately. Not a markdown table you need to parse manually."
  }
];

export const VALUE_PROPS = [
  {
    icon: "Crosshair",
    title: "Context-aware",
    text: 'Say "Tokyo ramen shop" and get Yen pricing, Japanese names, realistic menu items. Say "Nairobi logistics" and get KES amounts, Kenyan cities, local carriers — not generic Western filler.'
  },
  {
    icon: "Link",
    title: "Relationally valid",
    text: "Foreign keys hold. Distributions make sense. Your demo won't break on a cross-table question."
  },
  {
    icon: "RefreshCcw",
    title: "Build > Test > Demo",
    text: "Start in development, stress-test your queries, then demo with data prospects recognize."
  }
];
